import axios from 'axios';
import { VIEW_AS_HEADER, readViewAs, isPeakConsolePath } from './viewAs';
import { parseTenantPath, withTenant } from './tenantPath';
import { roundWhole } from './money';
// Imperative, non-hook API (see components/Toast.jsx) — safe to call from this
// module. Used to report export failures, which have no caller to catch them.
import { toast } from '../components/Toast';

// In production the React build is served by the same Express server,
// so all API calls are same-origin. In development, the API is on :3001.
//
// REACT_APP_API_BASE overrides the dev target. Needed when more than one
// worktree runs at once: the second checkout can't bind :3001, and without an
// override its client silently talks to the FIRST worktree's API — i.e. you
// debug one branch against another branch's database. Unset in normal use.
const BASE = process.env.NODE_ENV === 'production'
  ? ''
  : (process.env.REACT_APP_API_BASE || `http://${window.location.hostname}:3001`);
const api = axios.create({ baseURL: `${BASE}/api` });

// Attach JWT + selected restaurant on every request. The restaurant is THIS
// tab's URL, never storage: that is shared, so another
// tab switching restaurant would redirect this tab's writes (#179).
export const attachSession = (config) => {
  const token = localStorage.getItem('token');
  const rid   = parseTenantPath(window.location.pathname).rid;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (rid) config.headers['X-Restaurant-Id'] = rid;
  else delete config.headers['X-Restaurant-Id'];
  // Read-only preview (utils/viewAs.js). Never from a Command Center tab.
  const viewAs = readViewAs();
  if (viewAs && rid && !isPeakConsolePath(window.location.pathname)) config.headers[VIEW_AS_HEADER] = viewAs;
  return config;
};
// A document may enter its first tenant from a global login/legacy route. After
// that it cannot send under another tenant: TenantUrlProvider reloads at that
// boundary. This fence also rejects async continuations before reload completes.
export function createDocumentTenantGuard() {
  let documentTenant = null;
  return (config) => {
    const rid = parseTenantPath(window.location.pathname).rid;
    if (documentTenant != null && rid !== documentTenant) {
      throw new axios.CanceledError('Restaurant changed; the previous page request was cancelled');
    }
    if (rid != null) documentTenant = rid;
    return attachSession(config);
  };
}
api.interceptors.request.use(createDocumentTenantGuard());

// Stale-tenant guard. When the active restaurant is switched, a page may still
// hold a by-id resource (supplier id, etc.) from the previous restaurant; the
// next fetch then carries the new X-Restaurant-Id with the old id and the
// server returns an ownership 403 ("אין גישה לספק זה" / "אין גישה למסמך זה").
// Instead of letting that reject surface as an unhandled error (a dev crash
// overlay; a "not found" view in prod for a document that exists), land the
// user on the suppliers list of the now-active restaurant. Covers every by-id
// endpoint uniformly. Only these specific ownership 403s are handled; all
// other errors propagate unchanged.
export const STALE_TENANT_ERRORS = Object.freeze(['אין גישה לספק זה', 'אין גישה למסמך זה']);
export const isStaleTenantError = (err) =>
  err?.response?.status === 403 && STALE_TENANT_ERRORS.includes(err?.response?.data?.error);

export const handleApiError = (err) => {
  const code = err?.response?.data?.code;
  if ((code === 'HQ_ONLY_SUPPLIER' || code === 'VIEW_AS_READ_ONLY') && err?.config?.method !== 'get') {
    toast(err.response.data.error, 'error');
  }
  if (err?.response?.status === 403 && err?.response?.data?.error === 'אין הרשאה למסעדה זו') {
    const rid = err.config?.headers?.['X-Restaurant-Id'];
    if (rid) window.dispatchEvent(new CustomEvent('peak:tenant-denied', { detail: String(rid) }));
  }
  if (isStaleTenantError(err)) {
    // Stale-tenant access: a by-id request carried a supplier id from the
    // restaurant the user just switched away from. Switching restaurants is a
    // normal action, so redirect SILENTLY to the new restaurant's suppliers
    // list — no red error toast (that read as a bug to the user).
    const { rid } = parseTenantPath(window.location.pathname);
    const destination = withTenant(rid, '/suppliers');
    if (window.location.pathname !== destination) {
      window.location.assign(destination);
    }
    // Swallow: we're navigating away, so the caller should not see a reject
    // (which would otherwise become an unhandled rejection / crash overlay).
    return new Promise(() => {});
  }
  return Promise.reject(err);
};

api.interceptors.response.use((res) => res, handleApiError);

export const tenantOfApi = {
  lookup: (type, id) => api.get('/tenant-of', { params: { type, id }, timeout: 5000 }),
};

export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me:    () => api.get('/auth/me'),
  forgotPassword:       (email)    => api.post('/auth/forgot-password', { email }),
  validateResetToken:   (token)    => api.get('/auth/reset-password/validate', { params: { token } }),
  resetPassword:        (token, password) => api.post('/auth/reset-password', { token, password }),
};

export const adminApi = {
  getRestaurants:   ()       => api.get('/admin/restaurants'),
  createRestaurant: (data)   => api.post('/admin/restaurants', data),
  updateRestaurant: (id, d)  => api.put(`/admin/restaurants/${id}`, d),
  addRestaurantAlias: (id, name, docBusinessId = null) =>
    api.post(`/admin/restaurants/${id}/aliases`, { name, doc_business_id: docBusinessId }),
  ignoreRecipientBid: (id, businessId, docName = '', confirmOwner = false) =>
    api.post(`/admin/restaurants/${id}/ignored-bids`, { business_id: businessId, doc_name: docName, confirm_owner: confirmOwner }),
  // Networks (chains): each returns/accepts { name, restaurant_ids:[] }.
  getNetworks:      ()       => api.get('/admin/networks'),
  createNetwork:    (data)   => api.post('/admin/networks', data),
  updateNetwork:    (id, d)  => api.put(`/admin/networks/${id}`, d),
  deleteNetwork:    (id)     => api.delete(`/admin/networks/${id}`),
  getUsers:         ()       => api.get('/admin/users'),
  createUser:       (data)   => api.post('/admin/users', data),
  updateUser:       (id, d)  => api.put(`/admin/users/${id}`, d),
  deleteUser:       (id)     => api.delete(`/admin/users/${id}`),
  // Returns { index_active, groups: [{ restaurant_id, supplier_id, doc_num,
  // count, docs:[{ id, doc_type, date, total, status, supplier_name,
  // restaurant_name, created_at }] }] }. When groups is empty, server restart
  // activates the unique-doc-num index.
  getDuplicateDocs: ()       => api.get('/admin/duplicate-documents'),
  exportCatalogConsistency: () => downloadXlsx('/admin/catalog-consistency/export', {}, 'catalog_consistency'),
};

// Peak Command Center — the internal ops console. Every endpoint here is
// cross-tenant: the server ignores X-Restaurant-Id (which the interceptor above
// omits from a console tab, whose URL carries no restaurant). Reads
// only; creates/updates reuse adminApi, so there is exactly one writer per
// resource, except restaurant SETTINGS — the console writes those directly so
// ops never has to switch tenant to fix a Dokka address. See
// routes/commandCenter.js for the rule this rests on.
export const commandCenterApi = {
  // { query, users, restaurants, networks, suppliers, documents } — each group
  // is { items: [], total } so the UI can say "ועוד N".
  search:        (q, limit) => api.get('/command-center/search', { params: { q, limit } }),
  // Every overview tile in one payload; each block carries a total AND the age
  // of its oldest item, because a count alone can't say who to call first.
  getOverview:   ()     => api.get('/command-center/overview'),
  getQueue:      (view, restaurantIds = [], dateRange = {}, docTypes = [], { includeHandled = false, deletedBy = null, deletedSuppliers = [], deletedSort = null } = {}) => api.get('/command-center/queues', {
    // Filtering happens server-side, before the row limit — see lib/ccQueues.
    params: {
      view,
      ...(restaurantIds.length ? { restaurant: restaurantIds.join(',') } : {}),
      ...(dateRange.from ? { from: dateRange.from } : {}),
      ...(dateRange.to ? { to: dateRange.to } : {}),
      ...(docTypes.length ? { doc_type: docTypes.join(',') } : {}),
      ...(includeHandled ? { include_handled: '1' } : {}),
      // מחוקים tab: 'people' | 'system' — who deleted it (#307).
      ...(deletedBy ? { deleted_by: deletedBy } : {}),
      // Its ספק filter: JSON, because a supplier's name can hold a comma.
      ...(deletedSuppliers.length ? { supplier: JSON.stringify(deletedSuppliers) } : {}),
      ...(deletedSort ? { deleted_sort: deletedSort } : {}),
    },
  }),
  // סטטיסטיקות → מסמכים — documents per type and per supplier × type,
  // platform-wide. An empty range means all time (see lib/ccStats.js).
  getDocumentStats: (dateRange = {}) => api.get('/command-center/stats/documents', {
    params: {
      ...(dateRange.from ? { from: dateRange.from } : {}),
      ...(dateRange.to ? { to: dateRange.to } : {}),
    },
  }),
  // סטטיסטיקות → איכות קריאה — review quality, review time, deletions, AI
  // usage and the §9 baseline (lib/ccExtractionStats.js). Empty range = the
  // server's last 30 days; empty doc types = its default (חשבונית, תעודת משלוח).
  getExtractionStats: ({ from, to, docTypes = [], restaurantId, baselineFrom, baselineTo } = {}) =>
    api.get('/command-center/stats/extraction', {
      params: {
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(docTypes.length ? { doc_types: docTypes.join(',') } : {}),
        ...(restaurantId ? { restaurant_id: restaurantId } : {}),
        ...(baselineFrom ? { baseline_from: baselineFrom } : {}),
        ...(baselineTo ? { baseline_to: baselineTo } : {}),
      },
    }),
  // סטטיסטיקות → ספקים: the list, and one supplier's settings + prices. The
  // key is the supplier's normalised name — free text, so it rides a param.
  getSupplierStats:  () => api.get('/command-center/stats/suppliers'),
  getSupplierDetail: (key) => api.get('/command-center/stats/suppliers/detail', { params: { key } }),
  // חשודים כאותו ספק — one pair's evidence, a decision on it, the log, undo
  // (lib/supplierIdentity.js). Keys are normName'd names, so they ride params/body.
  getSupplierPair:        (a, b) => api.get('/command-center/stats/suppliers/pair', { params: { a, b } }),
  decideSupplierPair:     (body) => api.post('/command-center/stats/suppliers/decisions', body),
  getSupplierDecisions:   () => api.get('/command-center/stats/suppliers/decisions'),
  undoSupplierDecision:   (id) => api.post(`/command-center/stats/suppliers/decisions/${id}/undo`),
  // הזנת ח.פ. ידנית — supplier rows by name fragment, and setting one's ח.פ.
  getSupplierIds: (q) => api.get('/command-center/supplier-ids', { params: { q } }),
  setSupplierId:  (supplierId, businessId) => api.put(`/command-center/supplier-ids/${supplierId}`, { business_id: businessId }),
  // החלת דוח ח.פ. — upload the backfill script's report.json; a preview unless
  // apply is true (lib/supplierIdBackfill.js#applyReport).
  supplierIdBackfill: (file, apply = false) => {
    const form = new FormData();
    form.append('report', file);
    return api.post('/command-center/supplier-ids/backfill', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: apply ? { apply: 1 } : {},
    });
  },
  // שורות — document lines across restaurants, and marking them as adjustments
  // (server/lib/ccLineItems.js). supplierIds / restaurantIds are database ids.
  // allSuppliers + excludeSupplierIds = "every supplier but these".
  getLineItems: ({ q = '', supplierIds = [], allSuppliers = false, excludeSupplierIds = [], restaurantIds = [], kind = 'all', limit, before } = {}) =>
    api.get('/command-center/line-items', {
      params: {
        ...(q ? { q } : {}),
        ...(supplierIds.length ? { supplier: supplierIds.join(',') } : {}),
        ...(allSuppliers ? { all_suppliers: '1' } : {}),
        ...(allSuppliers && excludeSupplierIds.length ? { supplier_not: excludeSupplierIds.join(',') } : {}),
        ...(restaurantIds.length ? { restaurant: restaurantIds.join(',') } : {}),
        ...(kind !== 'all' ? { kind } : {}),
        ...(limit ? { limit } : {}),
        ...(before ? { before } : {}),
      },
    }),
  // The tab counters — lines per classification for the same filter.
  getLineItemCounts: ({ q = '', supplierIds = [], allSuppliers = false, excludeSupplierIds = [], restaurantIds = [] } = {}) =>
    api.get('/command-center/line-items/counts', {
      params: {
        ...(q ? { q } : {}),
        ...(supplierIds.length ? { supplier: supplierIds.join(',') } : {}),
        ...(allSuppliers ? { all_suppliers: '1' } : {}),
        ...(allSuppliers && excludeSupplierIds.length ? { supplier_not: excludeSupplierIds.join(',') } : {}),
        ...(restaurantIds.length ? { restaurant: restaurantIds.join(',') } : {}),
      },
    }),
  getLineItemSuppliers: () => api.get('/command-center/line-items/suppliers'),
  // Every live line in the system by classification (the page's summary line).
  getLineItemSummary: () => api.get('/command-center/line-items/summary'),
  // כללי סיווג (#300) — automatic classification rules for new lines.
  getLineKindRules:      () => api.get('/command-center/line-items/rules'),
  // `config.signal` aborts a preview the admin has typed past — the server stops
  // its scan when the request goes away.
  previewLineKindRule:   (rule, config) => api.post('/command-center/line-items/rules/preview', rule, config),
  createLineKindRule:    (rule) => api.post('/command-center/line-items/rules', rule),
  setLineKindRuleActive: (id, active) => api.patch(`/command-center/line-items/rules/${id}`, { active }),
  deleteLineKindRule:    (id) => api.delete(`/command-center/line-items/rules/${id}`),
  // What the shell asks when its tab is in front: { message, can_edit }. Changes nothing;
  // markDailyMessageShown ({ first }) is what uses up today's showing.
  getTodaysDailyMessage: () => api.get('/command-center/daily-message/today'),
  markDailyMessageShown: () => api.post('/command-center/daily-message/shown'),
  getDailyMessage:   () => api.get('/command-center/daily-message'),
  saveDailyMessage:  (message) => api.put('/command-center/daily-message', message),
  // Without apply: the plan only. With apply: creates the exact rules.
  lineKindRulesFromClassified: (apply = false) =>
    api.post('/command-center/line-items/rules/from-classified', null, { params: apply ? { apply: 1 } : {} }),
  // lineKind 'adjustment' marks (adjustmentType optional), '' unmarks.
  classifyLineItems: (ids, lineKind, adjustmentType = '') =>
    api.post('/command-center/line-items/classify', { ids, line_kind: lineKind, adjustment_type: adjustmentType }),
  // "טופל" on the לא תקין queue — hides the document from the console only.
  // view: 'invalid' | 'unlinked'
  markHandled:   (view, id, note) => api.post(`/command-center/queues/${view}/${id}/handled`, { note }),
  // מחוקים — undo a soft delete. The server uses the document's own restaurant.
  restoreDeleted: (id) => api.post(`/command-center/deleted/${id}/restore`),
  // The read-only viewer: facts, lines and pages of a waiting document, and
  // one page's bytes — every page, local or Cloudinary, comes through here.
  getDeleted:     (id) => api.get(`/command-center/deleted/${id}`),
  getDeletedPage: (id, page) => api.get(`/command-center/deleted/${id}/files/${page}`, { responseType: 'blob' }),
  unmarkHandled: (view, id) => api.delete(`/command-center/queues/${view}/${id}/handled`),
  getPeople:     ()   => api.get('/command-center/people'),
  getPerson:     (id) => api.get(`/command-center/people/${id}`),
  resendInvite:  (id) => api.post(`/command-center/people/${id}/resend-invite`),
  getCustomers:  ()   => api.get('/command-center/customers'),
  getCustomer:   (id) => api.get(`/command-center/customers/${id}`),
  // Edit a customer's VAT / payment-tracking / Dokka settings for ANY
  // restaurant, without switching tenant. Same table and same validation as
  // the app's /settings screen (server: lib/restaurantSettings), so pass the
  // `expected_updated_at` you loaded — a concurrent save 409s rather than
  // silently overwriting the other person.
  updateCustomerSettings: (id, data) => api.put(`/command-center/customers/${id}/settings`, data),

  // מיני-CRM — Peak's own books. One GET carries the month's board, its summary
  // and the 12-month trend together, because the screen shows all three at once
  // and three separate round trips could disagree mid-load.
  // AMOUNTS ARE SHEKELS on the wire; the server keeps agorot (server/lib/crm.js).
  // The whole screen for a date range: the customer directory, one row per
  // month for the trends, and the span's totals. `from`/`to` are ISO DAYS (what
  // the shared PeriodPicker speaks); the server resolves them to whole months —
  // the only granularity this data has — and echoes back the span it used.
  getCrm: (from, to) => api.get('/command-center/crm', { params: { from, to } }),
  // The customer's standing record — { monthly_fee?, contact_name?, folder_url?,
  // notes?, started_month?, ended_month? }. PARTIAL: send only what you are
  // changing; anything omitted keeps its value, so a control that edits one
  // field cannot blank the others. The fee applies GOING FORWARD — months
  // already recorded keep what they were billed at, so a price change is never
  // retroactive. started_month/ended_month are '' to clear — see
  // server/lib/crm.js#startMonthOf and #isActiveIn for what they drive: every
  // figure on the page, not just this row.
  saveCrmCustomer: (id, patch) =>
    api.put(`/command-center/crm/customers/${id}`, patch),
  // The payment board: customers × the twelve months of one CALENDAR year, each
  // cell carrying its own state and amount. Year-scoped on purpose — it does not
  // follow the page's date range, because "what did they pay this year" is a
  // calendar question.
  getCrmBoard: (year) => api.get('/command-center/crm/board', { params: { year } }),
  // One month for one customer — what the charge editor loads when you change
  // the month inside it, so switching to July cannot offer September's amount.
  getCrmCharge: (id, month) =>
    api.get(`/command-center/crm/customers/${id}/charges/${month}`),
  // Send only { paid } to tick a month off: the amount then comes from the
  // existing row, or from the current fee if the month was never touched, and
  // paid_at defaults to today. Pass `paid_at` (ISO YYYY-MM-DD) to record when
  // the money actually landed rather than when it got written down.
  saveCrmCharge: (id, month, data) =>
    api.put(`/command-center/crm/customers/${id}/charges/${month}`, data),
  // PEAK's own headcount per month, for הכנסות לעובד — one calendar year, each
  // month with `employees` (the default where nobody entered one: `is_default`)
  // and `prelaunch` for months before go-live, which cannot be set.
  getCrmHeadcount: (year) => api.get('/command-center/crm/headcount', { params: { year } }),
  // { 'YYYY-MM': n | null } — null clears a month back to the default. All-or-nothing.
  saveCrmHeadcount: (months) => api.put('/command-center/crm/headcount', { months }),
};

// Download a file from an auth-protected endpoint. window.open can't be used
// for these — it opens a fresh GET without the axios interceptor's
// Authorization / X-Restaurant-Id headers, so the request hits the server
// unauthenticated and 401s silently. This helper requests as a blob (axios
// carries the headers), then triggers a save with the server's filename
// (parsed from Content-Disposition, falling back to a sensible default).
// Trigger a browser "save file" from a Blob, reliably across browsers and OSes
// (Chrome/Edge/Firefox/Safari on Windows + macOS). Two cross-browser gotchas:
// (1) the anchor must be attached to the DOM before click (Firefox), and
// (2) the object URL must be revoked (and the anchor removed) a tick LATER —
// doing it synchronously right after click aborts the download in Firefox and
// some Windows browsers. Legacy IE/old Edge fall back to msSaveBlob.
function saveBlob(blob, filename) {
  if (typeof navigator !== 'undefined' && navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, filename);
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    if (a.parentNode) a.parentNode.removeChild(a);
  }, 1000);
}

async function downloadAuthenticatedFile(urlPath, fallbackName, params) {
  const r = await api.get(urlPath, { params, responseType: 'blob' });
  let filename = fallbackName;
  const cd = r.headers['content-disposition'] || '';
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  const ascii = /filename="([^"]+)"/i.exec(cd);
  if (utf8)       filename = decodeURIComponent(utf8[1]);
  else if (ascii) filename = ascii[1];
  saveBlob(r.data, filename);
}

export const suppliersApi = {
  getAll:       (params) => api.get('/suppliers', { params }),
  markDeliveryNotes: (id) => api.post(`/suppliers/${id}/uses-delivery-notes`),
  // With { date_from, date_to } naming one calendar month the response also
  // carries that month's payment `lifecycle` (the /supplier/:id header).
  getOne:       (id, params) => api.get(`/suppliers/${id}`, params ? { params } : undefined),
  create:       (data)   => api.post('/suppliers', data),
  // supplierId / businessId let the server also list likely same-restaurant
  // duplicates that would stay visible (same name, ח.פ. or alias).
  hqOnlyImpact: (name, { supplierId, businessId } = {}) => api.get('/suppliers/hq-only-impact', {
    params: { name, ...(supplierId ? { supplier_id: supplierId } : {}), ...(businessId ? { business_id: businessId } : {}) },
  }),
  hqOnlySummary: (params) => api.get('/suppliers/hq-only-summary', { params }),
  update:       (id, d)  => api.put(`/suppliers/${id}`, d),
  updatePaymentTerms: (id, d) => api.patch(`/suppliers/${id}/payment-terms`, d),
  updateAliases: (id, aliases) => api.patch(`/suppliers/${id}/aliases`, { aliases }),
  updateInvoiceRules: (id, invoice_rules) => api.patch(`/suppliers/${id}/invoice-rules`, { invoice_rules }),
  setInvoiceDiscount: (id, body) => api.patch(`/suppliers/${id}/invoice-discount`, body),
  delete:       (id)     => api.delete(`/suppliers/${id}`),
  // The payment approval is filed under a month key, and the server accepts only a range that
  // names exactly one calendar month — anything else is a 400 rather than the
  // old guess at date_from's month. Send BOTH ends whenever either is set: a
  // one-sided range ("עד 30/06/26", reachable from the day view) names no
  // month, and omitting date_to would let the server read it as "no range".
  approve:      (id, dateFrom, dateTo) =>
    api.post(
      `/suppliers/${id}/approve`,
      (dateFrom || dateTo) ? { date_from: dateFrom || '', date_to: dateTo || '' } : {}
    ),
  // Delivery notes not rolled up under their חשבונית מרכזת — one row per
  // supplier with the count, the amount and the invoice to link them to.
  // Admin-only on the server (403 for anyone else): only an admin can act on
  // it, and a flag shown to someone who cannot clear it is worse than none.
  unlinkedDeliveryNotes: (params) => api.get('/suppliers/unlinked-delivery-notes', { params }),
  // Open notes a plain invoice dated `date` (DD/MM/YYYY) might be covering —
  // { count, months } for the entry screen's "הפוך לחשבונית מרכזת" chip.
  // Notes on this supplier's documents in the list row's window ({ date_from,
  // date_to } or { due_date }) — the ones the /suppliers הערות cell counts.
  documentComments: (id, params) => api.get(`/suppliers/${id}/document-comments`, { params }),
  openDeliveryNotes: (id, date) => api.get(`/suppliers/${id}/open-delivery-notes`, { params: { date } }),
  exportExcel:  (params) => downloadXlsx('/suppliers/export/excel', params, 'suppliers'),
  mergePreview: (fromId, toId) => api.get('/suppliers/merge/preview', { params: { from_id: fromId, to_id: toId } }),
  // overwriteBusinessId: replace the target's different ח.פ. with the source's —
  // only after the admin was warned (MergeSuppliersPage).
  merge:        (fromId, toId, { overwriteBusinessId = false, hqOnly } = {}) => api.post('/suppliers/merge', {
    from_id: fromId,
    to_id: toId,
    overwrite_business_id: overwriteBusinessId,
    ...(hqOnly === 0 || hqOnly === 1 ? { hq_only: hqOnly } : {}),
  }),
};

export const documentsApi = {
  getAll:        (params)     => api.get('/documents', { params }),
  getBySupplier: (supplierId) => api.get(`/documents/supplier/${supplierId}`),
  getOne:        (id)         => api.get(`/documents/${id}`),
  // 300s: the server may make up to 3 sequential Claude calls (attempt 1 +
  // parse-retry + supplier rule-aware reparse) plus a Cloudinary upload. The
  // old 120s equalled a single Claude call, so a valid slow request could be
  // aborted client-side while the server was still working.
  upload:        (formData)   => api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300_000 }),
  save:          (data)       => api.post('/documents', data),
  update:        (id, data)   => api.put(`/documents/${id}`, data),
  updateStatus:  (id, status, review) => api.put(`/documents/${id}/status`, review ? { status, review } : { status }),
  // A soft delete: body { reason, reason_note?, review? } (server/lib/softDelete.js).
  delete:        (id, body)   => api.delete(`/documents/${id}`, { data: body }),
  // Moving a draft to another restaurant (#304, server/lib/documentMove.js).
  // The preview lists the suggested restaurants and, with a target, the
  // supplier it lands with or the blockers; the move takes { target_restaurant_id, review? }.
  movePreview:   (id, target) => api.get(`/documents/${id}/move-preview`, { params: target ? { target } : {} }),
  move:          (id, body)   => api.post(`/documents/${id}/move`, body),
  // A return draft on the same scan as a combined החברה המרכזית sheet (#367).
  createReturnNote: (id, docNum) => api.post(`/documents/${id}/return-note`, { doc_num: docNum }),
  exportExcel:   (supplierId, params) => downloadXlsx(`/documents/supplier/${supplierId}/export`, params, 'documents'),
  exportAll:     (params)     => downloadXlsx('/documents/export/all', params, 'documents'),
  exportPayments:    (supplierId, params) => downloadXlsx(`/documents/supplier/${supplierId}/export-payments`, params, 'תשלומים'),
  exportAllPayments: (params)             => downloadXlsx('/documents/export/payments', params, 'דוח_תשלומים'),
  unlinkedDeliveryNotes: (invoiceId) => api.get(`/documents/${invoiceId}/unlinked-delivery-notes`),
  // What a חשבונית מרכזת bills for, against what exists in the system.
  coverage:              (invoiceId) => api.get(`/documents/${invoiceId}/coverage`),
  // markInvalid: the admin saved a link set that does not reconcile and chose
  // to record that on the invoice. The server applies it in the SAME
  // transaction as the links, and answers with what it actually did
  // ({ status, marked_invalid }) — it will not flag a document that is still
  // a draft. Never send a bare truthy value: the route accepts === true only.
  // confirmByAmount: "same notes, different numbering" — the server accepts it
  // only when the saved notes reconcile with the invoice net.
  // decidedInvalid: the window on a draft already saved לא תקין over this finding
  // (#381), so no flip is left, only the marker that lets a resolved save lift it.
  saveLinks:             (invoiceId, deliveryNoteIds, markInvalid = false, confirmByAmount = false, decidedInvalid = false) =>
    api.put(`/documents/${invoiceId}/links`, {
      delivery_note_ids: deliveryNoteIds,
      mark_invalid: markInvalid === true,
      ...(confirmByAmount === true ? { confirm_by_amount: true } : {}),
      ...(decidedInvalid === true ? { decided_invalid: true } : {}),
    }),
  // "שמור סימונים בטיוטה": remember the ticks on a draft מרכזת without linking
  // them, so no money moves — see server/lib/pendingLinks.js.
  // seenNoteIds: every note the window listed, which is what lets a note arriving
  // later be told apart from one left unticked on purpose.
  savePendingLinks:      (invoiceId, deliveryNoteIds, seenNoteIds) =>
    api.put(`/documents/${invoiceId}/pending-links`, { delivery_note_ids: deliveryNoteIds, seen_note_ids: seenNoteIds }),
  // "This note belongs to no מרכזת we have" — the off-switch for the unlinked
  // indicator, so the flag can reach zero instead of standing forever on a note
  // that really belongs to next month's invoice. Admin-only, like saveLinks.
  dismissLink:   (noteId, invoiceId) => api.post(`/documents/${noteId}/dismiss-link`, { invoice_id: invoiceId }),
  undismissLink: (noteId)            => api.delete(`/documents/${noteId}/dismiss-link`),
  // Admin: a scanned page's display rotation (0/90/180/270, clockwise). The
  // stored file is never rewritten — see server/lib/pageRotation.js.
  rotatePage:    (id, pageNumber, rotation) => api.put(`/documents/${id}/files/${pageNumber}/rotation`, { rotation }),
  // Pre-save duplicate check: { exists, id?, doc_type?, date? } for the
  // first (supplier_id, doc_num) match in the current restaurant scope.
  // docType lets the server apply the same exemptions as the save itself
  // (a כרטסת is never a duplicate — see utils/docTypes.js).
  checkDuplicate: (supplierId, docNum, docType) =>
    api.get('/documents/check-duplicate', { params: { supplier_id: supplierId, doc_num: docNum, doc_type: docType } }),
  // Drafts (admin-only on the server — non-admin callers get 403).
  // gatePreview: the owner's preview of the gate colours (utils/gatePreview.js);
  // the server honours it only for a login on its preview list.
  getDrafts:      ({ gatePreview = false } = {}) => api.get('/documents/drafts', gatePreview ? { params: { gate_preview: 1 } } : undefined),
  gatePreview:    ()    => api.get('/documents/gate-preview'),
  getDraftsCount: ()    => api.get('/documents/drafts/count'),
  // body: { entry, review } — how the approval was reached (server/lib/reviewLog.js); optional.
  approve:        (id, body) => api.post(`/documents/${id}/approve`, body),
  // The draft gate's exposure record (stage 3): the screen committed this draft.
  gateView:       (id, body) => api.post(`/documents/${id}/gate-view`, body),
  // body: { review: { review_seconds } } — the batch's one time, shared by the server; optional.
  approveAll:     (body) => api.post('/documents/drafts/approve-all', body),
  forwardToDokka: (id)  => api.post(`/documents/${id}/forward-to-dokka`),
  forwardStatus:  (id)  => api.get(`/documents/${id}/forward-status`),
  // Manual "run now" backfill of last-known catalog prices onto this invoice's
  // linked delivery notes that still have zero-priced lines. Returns
  // { documents_updated, lines_filled, lines_from_invoice, lines_still_zero }.
  backfillDeliveryPrices: (id) => api.post(`/documents/${id}/backfill-delivery-prices`),
  // Per-supplier invoice-rule learning: compare what Claude parsed vs what
  // the user saved and suggest a rule if a known pattern matches.
  detectRule:     (data) => api.post('/documents/detect-rule', data),
  // Bulk-download every original file for the currently filtered documents as
  // one ZIP. Same authed-blob mechanism as the Excel export (reliable in dev +
  // prod). Throws an Error carrying the server's Hebrew message on 400/404.
  downloadFilesZip: (params) => downloadFilesZipBlob(params),
};

async function downloadFilesZipBlob(params) {
  try {
    await downloadAuthenticatedFile('/documents/export/files-zip', 'מסמכים.zip', params);
  } catch (err) {
    // The 404 (no files) / 400 (too many) bodies arrive as a Blob because we
    // asked for responseType 'blob'; read it back to surface the Hebrew message.
    const data = err?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text().catch(() => '');
      try { throw new Error(JSON.parse(text).error || 'שגיאה בהורדת הקבצים'); }
      catch (parsed) { throw new Error(parsed.message || 'שגיאה בהורדת הקבצים'); }
    }
    throw err;
  }
}

// Download an xlsx blob with auth. `window.open()` bypasses the axios
// interceptor that attaches our JWT and X-Restaurant-Id headers, so the server
// would 401 silently. Routing the GET through `api.get(..., responseType:
// 'blob')` keeps the headers attached, and we recover the server-side
// filename from Content-Disposition (UTF-8 first, ASCII fallback).
//
// FAILURES ARE REPORTED, NOT THROWN. Every export button in the app calls this
// from an onClick that does not await it, so a rejection here had nowhere to go
// and surfaced as "Uncaught (in promise) AxiosError" — a dev-overlay runtime
// error in development and a silently dead button in production. The user
// clicked "ייצוא" and simply nothing happened.
//
// The server's error body arrives as a Blob (we asked for responseType 'blob'),
// so it is read back to recover the Hebrew message the way downloadFilesZipBlob
// already does; a network failure has no body at all and falls back to a plain
// connection message.
// `body` (optional) turns it into a POST — for a filter too big for a URL.
async function downloadXlsx(path, params, fallbackName, body) {
  try {
    const r = body === undefined
      ? await api.get(path, { params, responseType: 'blob' })
      : await api.post(path, body, { params, responseType: 'blob' });
    let filename = `${fallbackName}_${new Date().toISOString().split('T')[0]}.xlsx`;
    const cd = r.headers['content-disposition'] || '';
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(cd);
    const ascii = /filename="([^"]+)"/i.exec(cd);
    if (utf8) filename = decodeURIComponent(utf8[1]);
    else if (ascii) filename = ascii[1];
    saveBlob(r.data, filename);
  } catch (err) {
    toast(await xlsxErrorMessage(err), 'error');
  }
}

// Best-effort Hebrew message for a failed export.
async function xlsxErrorMessage(err) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    const text = await data.text().catch(() => '');
    try {
      const parsed = JSON.parse(text);
      if (parsed?.error) return parsed.error;
    } catch { /* not JSON — fall through to the generic messages below */ }
  }
  if (err?.response?.status === 403) return 'אין הרשאה להפיק את הדוח';
  // No response at all = the request never reached the server.
  if (!err?.response) return 'אין חיבור לשרת — הדוח לא הופק. בדקו את החיבור ונסו שוב';
  return 'שגיאה בהפקת הדוח';
}

// downloadXlsx, but surfacing the server's Hebrew message instead of a generic
// axios error. Because the request asks for responseType 'blob', a 4xx body
// arrives as a Blob rather than parsed JSON — same recovery as the ZIP export.
async function downloadXlsxOrThrow(path, params, fallbackName) {
  try {
    await downloadXlsx(path, params, fallbackName);
  } catch (err) {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text().catch(() => '');
      let message = 'שגיאה בייצוא';
      try { message = JSON.parse(text).error || message; } catch { /* not JSON — keep the default */ }
      throw new Error(message);
    }
    throw err;
  }
}

export const analysisApi = {
  getAll:        (params) => api.get('/analysis', { params }),
  // "Does this restaurant have ANY documents?" — the one thing the date-scoped
  // endpoints above cannot answer, so an empty spend screen can tell a brand-new
  // customer from a quiet month. Asked only from an already-empty screen.
  hasHistory:    ()       => api.get('/analysis/has-history'),
  // Grand total only, for the metric strip's previous-period delta. getAll runs
  // a price-history query per supplier that the strip has no use for.
  getTotal:      (params) => api.get('/analysis/total', { params }),
  getSupplier:   (id, p)  => api.get(`/analysis/supplier/${id}`, { params: p }),
  getProduct:    (id, p)  => api.get(`/analysis/supplier/${id}/product`, { params: p }),
  exportAll:     (params) => downloadXlsx('/analysis/export', params, 'analysis'),
  // Every purchase line across ALL suppliers. Can 413 when the period is too
  // wide — the server refuses rather than truncating, so the Hebrew message
  // has to reach the user instead of a silent short file.
  exportAllItems: (params) => downloadXlsxOrThrow('/analysis/export-items', params, 'analysis_items'),
  // exportSupplier / exportSupplierItems were removed with their buttons —
  // the per-supplier exports' server endpoints still exist but have no client
  // callers; page-level exporting goes through the header ExportMenu.
  exportProduct: (id, p)  => downloadXlsx(`/analysis/supplier/${id}/product/export`, p, 'analysis_product'),
  // Network (chain) consolidated reports. getNetwork → merged-by-name supplier
  // list; getNetworkBreakdown → per-branch rows for one supplier name (each
  // carries supplier_id so the existing getSupplier drill-down can be reused).
  // Networks the current user may view (admin: all; viewer: those covering a
  // restaurant they're assigned to). Drives the analysis-page network selector.
  getMyNetworks:       ()             => api.get('/analysis/networks'),
  getNetwork:          (networkId, p) => api.get(`/analysis/network/${networkId}`, { params: p }),
  getNetworkBreakdown: (networkId, p) => api.get(`/analysis/network/${networkId}/breakdown`, { params: p }),
  // `names` (optional): the merged supplier names on screen. POSTed in the
  // body — a few hundred Hebrew names overflow a GET URL (PR #177, Codex).
  exportNetwork:       (networkId, p, names) => downloadXlsx(`/analysis/network/${networkId}/export`, p, 'analysis_network',
    names ? { names } : undefined),
  // Network-wide fixed pricing (write-through to every branch's catalog).
  getNetworkProducts:        (networkId, p)    => api.get(`/analysis/network/${networkId}/products`, { params: p }),
  setNetworkCatalog:         (networkId, body) => api.post(`/analysis/network/${networkId}/catalog`, body),
  setNetworkSupplierDiscount:(networkId, body) => api.post(`/analysis/network/${networkId}/supplier-discount`, body),
  getClassificationSpend:      (params) => api.get('/analysis/classifications', { params }),
  // The classification rows for the full report at /analysis/reports/item-classifications.
  // Keyed on (from, to, supplier_ids) only — the supplier and product counts
  // beside each row are counted off getClassificationProducts, not served here.
  getClassificationReport:     (params) => api.get('/analysis/classifications/report', { params }),
  // One row per (supplier × product) behind those classifications — the report's
  // expansions. A SEPARATE call from getClassificationReport on purpose: it is
  // additionally keyed on `q`, which is typed and debounced, and folding the
  // two together would flash the classification table into `loading` on a keystroke
  // for figures `q` must not change.
  getClassificationProducts:   (params) => api.get('/analysis/classifications/products', { params }),
  // The documents behind one product row's מספר מסמכים count — what that cell
  // opens into when clicked. `params` carries supplier_id + catalog_id (or
  // sku/name when the row has no catalog match) straight off the product row
  // the client already has, so this can only ever return the exact document
  // set that produced that row's number.
  getClassificationProductDocuments: (params) => api.get('/analysis/classifications/product-documents', { params }),
  exportClassifications:      (params) => downloadXlsx('/analysis/classifications/export', params, 'classifications'),
  // The product-level export. downloadXlsxOrThrow, not downloadXlsx: this route
  // refuses an over-large window with a 413 carrying a Hebrew explanation, and
  // with the plain helper that body arrives as an unread Blob and ExportMenu
  // prints axios's English "Request failed with status code 413" into an RTL UI.
  // exportClassifications stays on downloadXlsx — that route has no cap to trip.
  exportClassificationProducts: (params) => downloadXlsxOrThrow('/analysis/classifications/export-products', params, 'classifications_products'),
  // המוצרים המובילים ברכש (behind the restaurant's top_products_enabled flag —
  // the server answers 403 when it is off). `limit` defaults to the card's 10;
  // the report passes 'all'. Orders take the row's supplier_id + product_key.
  getTopProducts:      (params) => api.get('/analysis/top-products', { params }),
  getTopProductOrders: (params) => api.get('/analysis/top-products/orders', { params }),
  // OrThrow: the route refuses an oversized file with a 413 message the
  // ExportMenu shows inline.
  exportTopProducts:   (params) => downloadXlsxOrThrow('/analysis/top-products/export', params, 'top_products'),
  // The second report (owner, 21/10/2026): the same file plus a הזמנות sheet —
  // one row per order behind every listed product.
  exportTopProductsWithOrders: (params) => downloadXlsxOrThrow('/analysis/top-products/export', { ...params, include_orders: '1' }, 'top_products_orders'),
  // getClassificationBySupplier (/analysis/classification/:id) and getClassificationTrend
  // (/analysis/classifications/trend) were removed with their last callers: the
  // report's expansion now reads products from the payload it already holds,
  // and nothing charted the trend. Both server routes still exist, unreferenced.
  priceChanges:           ()  => api.get('/analysis/price-changes'),
  markViewedPriceChanges: ()  => api.post('/analysis/price-changes/mark-viewed'),
  // Download via axios (so the JWT Authorization header is attached) then
  // trigger a client-side save. window.open would open a fresh GET without our
  // axios interceptor → no Authorization header → 401 → no download.
  exportPriceChanges:  async () => {
    const r = await api.get('/analysis/price-changes/export', { responseType: 'blob' });
    // Pull the filename out of Content-Disposition so we keep the server's
    // Hebrew name. Falls back to a sensible default if the header is missing.
    let filename = `price_changes_${new Date().toISOString().split('T')[0]}.xlsx`;
    const cd = r.headers['content-disposition'] || '';
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(cd);
    const ascii = /filename="([^"]+)"/i.exec(cd);
    if (utf8)  filename = decodeURIComponent(utf8[1]);
    else if (ascii) filename = ascii[1];
    saveBlob(r.data, filename);
  },
};

export const creditsApi = {
  getAll:          (params)   => api.get('/credits', { params }),
  update:          (id, data) => api.put(`/credits/${id}`, data),
  getCandidateDocs:(supplierId) => api.get(`/credits/candidate-docs/${supplierId}`),
  getOpenCount:    (supplierId) => api.get(`/credits/open/${supplierId}`),
  getOpenList:     (supplierId) => api.get(`/credits/open-list/${supplierId}`),
  linkBulk:        (linkedCreditDocId, ids) => api.post('/credits/link-bulk', { linked_credit_doc_id: linkedCreditDocId, ids }),
  exportExcel:     (params)   => downloadXlsx('/credits/export/excel', params, 'credits'),
};

export const commentsApi = {
  getAll: (type, referenceId) => api.get('/comments', { params: { type, reference_id: referenceId } }),
  recipients: (type, referenceId) => api.get('/comments/recipients', { params: { type, reference_id: referenceId } }),
  create: (data) => api.post('/comments', data),
};

export const paymentsApi = {
  create:           (data) => api.post('/payments', data),
  createBulk:       (data) => api.post('/payments/bulk', data),
  getOverview:      (params) => api.get('/payments/overview', { params }),
  exportExcel:      (params) => downloadXlsx('/payments/overview/export', params, 'payments'),
  getBySupplier:    (sid)  => api.get(`/payments/supplier/${sid}`),
  delete:           (id)   => api.delete(`/payments/${id}`),
  getByDocument:    (docId)=> api.get(`/payments/document/${docId}`),
  deleteByDocument: (docId)=> api.delete(`/payments/document/${docId}`),
};

export const settingsApi = {
  get:    ()     => api.get('/settings'),
  update: (data) => api.put('/settings', data),

  // Spend-target defaults. A separate endpoint from the two above, not just a
  // separate wrapper: PUT /settings is admin-only, while these belong to
  // whoever runs the budget. getTargets also returns target_counts per
  // granularity, so the client can say what a switch would orphan.
  getTargets:    ()     => api.get('/settings/targets'),
  updateTargets: (data) => api.put('/settings/targets', data),
};

export const catalogApi = {
  // `pricesOnly` (#328): the matched row's prices and agreements only — no
  // "משוער" estimate, no history names. The draft review screen's ask; the
  // upload screen needs the full answer.
  match:           (supplierId, items, { pricesOnly = false } = {}) =>
    api.post(`/catalog/${supplierId}/match`, { items, ...(pricesOnly ? { prices_only: true } : {}) }),
  updateItem:      (supplierId, data)            => api.put(`/catalog/${supplierId}/item`, data),
  // Added by עץ מוצר: the add-ingredient picker needs a supplier's product list.
  getBySupplier:   (supplierId)                  => api.get(`/catalog/${supplierId}`),
  setItemClassification: (productId, classificationId)       => api.patch(`/catalog/${productId}/classification`, { classification_id: classificationId }),
  bulkSetClassification: (productIds, classificationId)      => api.patch('/catalog/classification/bulk', { product_ids: productIds, classification_id: classificationId }),
  unpricedItems:   ()                            => api.get('/catalog/unpriced-items'),
  setPriceAndFill: (supplierId, data)            => api.post(`/catalog/${supplierId}/price-and-fill`, data),
  // Classification tagging (the "סיווגים מוצרים" screen).
  coverage:        ()                            => api.get('/catalog/coverage'),
  suggestions:     ()                            => api.get('/catalog/suggestions'),
  applySuggestions:(apply)                       => api.post('/catalog/suggestions/apply', { apply }),
  products:        (params)                      => api.get('/catalog/products', { params }),
  // Classification automations (supplier → default item-classification).
  getRules:        ()                            => api.get('/catalog/classification-rules'),
  saveRules:       (rules)                        => api.put('/catalog/classification-rules', { rules }),
  // Point write/delete for ONE rule — the card's minimal saved-row view acts
  // on exactly one rule, never re-sending (or re-risking a conflict in) the
  // rest of the list the way saveRules does.
  saveRule:        (supplierId, classificationId) => api.put(`/catalog/classification-rules/${supplierId}`, { classification_id: classificationId }),
  deleteRule:      (supplierId)                   => api.delete(`/catalog/classification-rules/${supplierId}`),
};

export const classificationsApi = {
  getAll: ()        => api.get('/item-classifications'),
  create: (data)    => api.post('/item-classifications', data),
  // ALL FOUR paths are `/item-classifications`. update and delete were left on
  // the pre-rename `/classifications/:id` when the mount became
  // `/api/item-classifications` (server 3097198, client dbebf57) — that path is
  // not mounted, so both 404'd and the sheet reported "שגיאה במחיקה" /
  // "שגיאה בשינוי השם" for a request the server never had a route for.
  update: (id, d)   => api.put(`/item-classifications/${id}`, d),
  delete: (id)      => api.delete(`/item-classifications/${id}`),
};

// קטגוריות — the budget axis. A DIFFERENT categorisation from the one
// above: `classifications` answers "what kind of item is this", categories answer
// "which budget does this spend belong to". Spend reaches a category by
// MAPPING (an item-type classification, or a supplier) rather than by tagging.
export const categoryLinesApi = {
  getAll:      ()               => api.get('/category-lines'),
  create:      (data)           => api.post('/category-lines', data),
  update:      (id, d)          => api.put(`/category-lines/${id}`, d),
  delete:      (id)             => api.delete(`/category-lines/${id}`),
  summary:     (params)         => api.get('/category-lines/summary', { params }),
  // Per-period budget detail for /analysis — behind budget_module_enabled
  // (off → 403; the widget renders nothing rather than crash).
  weeklyBreakdown: (params)     => api.get('/category-lines/weekly-breakdown', { params }),
  mappings:    ()               => api.get('/category-lines/mappings'),
  // kind is 'classification' | 'supplier'; category_line_id null clears the rule.
  setMapping:  (kind, subjectId, categoryLineId) =>
    api.put(`/category-lines/mappings/${kind}/${subjectId}`, { category_line_id: categoryLineId }),
};

// הכנסות — manual revenue per category, behind the restaurant's
// budget_module_enabled flag (off → the server answers 403; consumers render
// nothing rather than crash). Amounts on the wire are SHEKELS; dates are ISO
// YYYY-MM-DD end to end (the one deliberate exception to DD/MM in documents).
export const revenueApi = {
  entries: (params)   => api.get('/revenue/entries', { params }),
  // { date_from, date_to, amount, overwrite? } — one amount for the restaurant,
  // no category. An overlap without overwrite → 409 { error, conflicts } for
  // the replace dialog.
  save:    (data)     => api.post('/revenue/entries', data),
  update:  (id, data) => api.put(`/revenue/entries/${id}`, data),
  // There is no saveBatch. It existed to collect one amount per category in a
  // single round trip — the model this feature was rebuilt to remove.
  delete:  (id)       => api.delete(`/revenue/entries/${id}`),
  summary: (params)   => api.get('/revenue/summary', { params }),
  coverage:(month)    => api.get('/revenue/coverage', { params: { month } }),
  // The full report behind the /analysis card: everything summary returns plus
  // a monthly series and a per-supplier split per category. Its own endpoint,
  // not a flag on summary — see routes/revenue.js.
  report:  (params)   => api.get('/revenue/report', { params }),
  exportReport:      (params) => downloadXlsx('/revenue/report/export', params, 'revenue_report'),
  // Can 413 on a wide range, so the menu needs the server's Hebrew message.
  exportReportItems: (params) => downloadXlsxOrThrow('/revenue/report/export-items', params, 'revenue_report_items'),
  // ימי סגירה — a restaurant-level day fact, NOT a ₪0 revenue entry. The PUT
  // replaces a whole month's set (send every closed date of the month, or []
  // to clear it), so the client never diffs and a repeat send is harmless.
  closedDays:        (month) => api.get('/revenue/closed-days', { params: { month } }),
  setClosedDays:     (data)  => api.put('/revenue/closed-days', data),
  addWeeklyClosedDays: (data) => api.post('/revenue/closed-days/weekly', data),
};

// Spend targets ("יעדי תקציב"). Every read defaults period_key to the CURRENT
// period server-side, so passing no params is a valid "how am I doing now".
//
// Progress figures count APPROVED documents only — draft_* / projected_* are
// returned alongside so the UI can show the pending band and explain why this
// differs from ניתוח רכש, which counts drafts.
export const targetsApi = {
  // Raw rows for the target-management list. `network_id` switches to that
  // chain's targets instead of the active restaurant's.
  list:     (params)        => api.get('/targets', { params }),
  // One scope: { scope, period, period_key, classification_id?, network_id? }.
  progress: (params)        => api.get('/targets/progress', { params }),
  // The annual coverage strip on /budget: every period of the year with
  // 'explicit' | 'inherited' | 'none'. { period?, year? }.
  coverage: (params)        => api.get('/targets/coverage', { params }),
  // EVERY classification + לא מסווג + a derived `rollup`, in one call. Drives the
  // NOTE: the classification axis no longer drives any screen — targets moved to
  // categories (see categoryLinesApi). These stay because the server still
  // supports the older scopes; nothing in the client calls them.
  //
  // `rollup` is the restaurant figure and is COMPUTED — the plain sum of the
  // classification budgets (all ex-VAT) — not a stored target. There is deliberately
  // no settable restaurant-wide target: it would be a second source of truth
  // that disagrees with the classifications the moment either side is edited.
  // `?with_previous=1` adds prev_actual_* for the sheet's prefill.
  summary:  (params)        => api.get('/targets/summary', { params }),
  // Chain roll-up + per-branch rows. Requires access to every branch.
  network:  (networkId, p)  => api.get(`/targets/network/${networkId}`, { params: p }),
  // Upsert. `apply_to` names how far the amount reaches: 'forward' (default —
  // this period on, ONE row, later periods inherit), 'this_period' (a one-off),
  // 'year' (rewrites the whole year, past periods included). Omitting apply_to
  // on an edit PRESERVES the row's existing reach.
  save:     (data)          => api.post('/targets', data),
  // The whole budget sheet in ONE call — all or nothing. Never loop over
  // `save`: the server is synchronous on a single connection, so N calls
  // serialise into N transactions and a failure halfway leaves a half-saved
  // sheet the UI cannot describe. `{ period?, period_key?, apply_to?,
  // rows: [{ scope, classification_id?, amount }] }`, where a null amount
  // clears that row's budget. A 400 carries `row_index`.
  saveBatch: (data)         => api.post('/targets/batch', data),
  update:   (id, d)         => api.put(`/targets/${id}`, d),
  remove:   (id)            => api.delete(`/targets/${id}`),
};

// "עץ מוצר" — dish costing. The restaurateur (a viewer) builds recipes from
// catalog products; cost is computed live on the server from current prices.
export const dishesApi = {
  getAll:           ()              => api.get('/dishes'),
  getOne:           (id)            => api.get(`/dishes/${id}`),
  create:           (data)          => api.post('/dishes', data),
  update:           (id, data)      => api.put(`/dishes/${id}`, data),
  delete:           (id)            => api.delete(`/dishes/${id}`),
  addIngredient:    (id, data)      => api.post(`/dishes/${id}/ingredients`, data),
  updateIngredient: (id, iid, data) => api.put(`/dishes/${id}/ingredients/${iid}`, data),
  deleteIngredient: (id, iid)       => api.delete(`/dishes/${id}/ingredients/${iid}`),
  // Unit-conversion for a product, stored once and reused across dishes.
  // Null when the product has no conversion yet — the add-ingredient modal
  // shows the conversion fields in that case instead of a read-only summary.
  getCosting:       (productCatalogId)       => api.get(`/dishes/costing/${productCatalogId}`),
  setCosting:       (productCatalogId, data) => api.put(`/dishes/costing/${productCatalogId}`, data),
  // Recent invoice lines for a product — shows "how it's bought today" so the
  // user can figure out the conversion without digging through documents.
  getProductPurchases: (productCatalogId) => api.get(`/dishes/product/${productCatalogId}/purchases`),
  // Phase-2 price-change alerts (both directions).
  getAlerts:       () => api.get('/dishes/alerts'),
  getAlertsCount:  () => api.get('/dishes/alerts/count'),
  markAlertsViewed:() => api.post('/dishes/alerts/mark-viewed'),
};

// Trigger a browser download of an uploaded file. Cloudinary URLs are
// cross-origin so `<a download>` is ignored by Chrome/Safari — instead we
// inject Cloudinary's `fl_attachment` flag, which makes their CDN respond
// with Content-Disposition: attachment. Local uploads now require auth, so
// we fetch via axios (carries JWT + X-Restaurant-Id) and save the blob.
export async function downloadDocFile(filePath) {
  if (!filePath) return false;
  const isHttp = /^https?:\/\//i.test(filePath);
  if (isHttp) {
    const href = filePath.includes('/upload/')
      ? filePath.replace('/upload/', '/upload/fl_attachment/')
      : filePath;
    const a = document.createElement('a');
    a.href = href;
    a.target = '_blank';
    a.rel = 'noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return true;
  }
  await downloadAuthenticatedFile(`/files/${encodeURIComponent(filePath)}`, filePath);
  return true;
}

// Fetch an uploaded file as a blob and return an object URL for inline
// display (iframe/img src). The server's /api/files/:filename route is
// authenticated, so we must carry the JWT via axios — direct URLs in src
// would 401 because browsers don't attach our localStorage token to img/iframe
// requests. Caller is responsible for URL.revokeObjectURL when the URL is
// no longer needed.
export async function fetchDocFileObjectUrl(filePath) {
  if (!filePath) return '';
  if (/^https?:\/\//i.test(filePath)) return filePath;
  const r = await api.get(`/files/${encodeURIComponent(filePath)}`, { responseType: 'blob' });
  return URL.createObjectURL(r.data);
}

export const formatCurrency = (n) =>
  new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

// Dish costing only. A single ingredient can cost a fraction of an agora (a gram
// of spice), and two decimals render that as ₪0.00 — an ingredient that reads as
// free. Money the restaurant actually pays or charges stays on formatCurrency;
// this is for the derived per-dish numbers.
// Dish money. Two decimals, like every other ₪ in the app — a dish costing
// ₪11.29 beside a menu price of ₪1,233.00 has to be read as the same kind of
// number, and "רווח גולמי ₪1,221.7140" is four digits of noise on a figure
// nobody reconciles to the agora.
//
// It used to be flat 4dp, for a real reason: an ingredient priced per gram is
// genuinely sub-agora, and rounding it would print ₪0.00 next to a product that
// is not free — the same class of lie as counting an unconverted ingredient as
// zero. So the precision is not fixed, it follows the number: anything that
// would round away to nothing keeps four decimals, everything else reads as
// money.
export const formatDishCurrency = (n) => {
  const v = Number(n) || 0;
  const tiny = v !== 0 && Math.abs(v) < 0.005;
  return new Intl.NumberFormat('he-IL', {
    style: 'currency', currency: 'ILS',
    minimumFractionDigits: tiny ? 4 : 2,
    maximumFractionDigits: tiny ? 4 : 2,
  }).format(v);
};

// Same money, no minor units. For headline figures that are being COMPARED
// rather than reconciled — "₪37,774 מתוך ₪70,898" is a proportion, and four
// digits of agorot across two numbers is noise the reader has to look past to
// see which is bigger. Anything an accountant might tie out keeps formatCurrency.
//
// It is also the formatter of the screens money is PAID from — /suppliers,
// /suppliers/paid, /credits, /credits/history and the payment dialogs (owner,
// 14/10/2026): the record keeps agorot, but a supplier is paid ₪233, not
// ₪232.80. Rounding goes through roundWhole (half up, float noise absorbed) rather
// than Intl's own, so the figure shown and the figure pre-filled in the
// payment field can never disagree.
export const formatCurrencyWhole = (n) =>
  new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', maximumFractionDigits: 0 }).format(roundWhole(n));

// Document / credit / supplier statuses share one colour table. The supplier-
// month PAYMENT lifecycle is deliberately NOT in here — see
// utils/paymentLifecycle.js and components/PaymentLifecycleBadge.jsx.
export const statusBadge = (status) => {
  const map = {
    'תקין': 'badge-green', 'לא תקין': 'badge-red',
    'טיוטה': 'badge-yellow', 'פתוח': 'badge-red', 'התקבל': 'badge-green',
    'בוטל': 'badge-gray', 'פעיל': 'badge-green', 'מושהה': 'badge-yellow', 'לא פעיל': 'badge-gray',
  };
  return map[status] || 'badge-gray';
};
